
import React, { useEffect, useState } from "react";
import { getCaseload } from "../../../services/analytics/analyticsService";
import { CaseloadResponse } from "../../../interfaces/analytics";
import AnalyticsFilters from "./AnalyticsFilters";
import Pagination from "../utility/Pagination";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from "../../../firebase/AuthProvider";

const CaseloadAnalytics: React.FC = () => {
  const { currentUser } = useAuth();
  const [authToken, setAuthToken] = useState<string>("");
  const [data, setData] = useState<CaseloadResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (currentUser) {
      currentUser.getIdToken().then(token => setAuthToken(token));
    }
  }, [currentUser]);

  useEffect(() => {
    if (!authToken) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await getCaseload(authToken, filters);
        setData(response);
        setCurrentPage(1);
      } catch (err) {
        setError("Failed to fetch Caseload data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authToken, filters]);

  const handleFiltersChange = (newFilters: any) => {
    setFilters(newFilters);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = data ? data.rows.slice(indexOfFirstItem, indexOfLastItem) : [];
  const totalPages = data ? Math.ceil(data.rows.length / itemsPerPage) : 0;

  if (loading) {
    return <div className="text-center p-4">Loading Caseload data...</div>;
  }

  if (error) {
    return <div className="text-center p-4 text-red-500">{error}</div>;
  }

  console.log('Data', data)

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Caseload Analytics</h2>
      <AnalyticsFilters filters={filters} onFiltersChange={handleFiltersChange} />
      {data ? (
        <>
          <div className="grid grid-cols-3 gap-4 mb-4 text-center">
            <div><h3 className="font-bold">As Of</h3><p>{new Date(data.asOf).toLocaleDateString()}</p></div>
            <div><h3 className="font-bold">Median Caseload</h3><p>{data.median}</p></div>
            <div><h3 className="font-bold">IQR</h3><p>{data.iqr}</p></div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.rows}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="scFullName" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#82ca9d" name="Caseload Count" />
            </BarChart>
          </ResponsiveContainer>
          <div className="overflow-x-auto mt-4">
            <table className="min-w-full bg-white border">
              <thead>
                <tr>
                  <th className="py-2 px-4 border-b">SC</th>
                  <th className="py-2 px-4 border-b">Count</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((row, index) => (
                  <tr key={index}>
                    <td className="py-2 px-4 border-b">{row.scFullName}</td>
                    <td className="py-2 px-4 border-b">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      ) : (
        <div className="text-center p-4">No Caseload data available.</div>
      )}
    </div>
  );
};

export default CaseloadAnalytics;
